import { addError } from '@/global/plugins/observability'
import {
  AccreditedEstablishmentsCode,
  Context
} from '@/global/plugins/observability/observability.constants'
import { encryptParamsClient } from '@/global/utils/sealed-params/sealed-params.client'
import { useSealedParamsContext } from '@/modules/accredited-establishments/contexts'
import {
  SEALED_FILTERS_PARAM,
  SEALED_FILTER_KEYS
} from '@/modules/accredited-establishments/constants/accredited-establishments'

type UpdateSealedSearchParamsProps = {
  pathname: string | null
  newParam: [string, string][]
}

/**
 * Cifra as chaves sensiveis no proprio navegador (Web Crypto) e escreve a URL
 * final direto com `history.replaceState`, sem navegacao nenhuma. Antes disto
 * o caminho passava por `router.replace` + redirect do `proxy.ts`, o que
 * disparava uma requisicao real a cada clique de filtro e recarregava a tela.
 * O texto puro nunca chega a ficar visivel: so o valor cifrado (`f`) vai pra
 * URL e pro historico.
 */
const useSealedSearchParams = () => {
  /*
   * `latestRequestIdRef` vem do context, nao de um `useRef` local: o hook e
   * instanciado de forma independente em `useFilters`, `useInitFilters` e
   * `AccreditedEstablishmentsView`, e as tres instancias precisam do mesmo
   * relogio de ordenacao pra serializar escritas concorrentes entre si (ex:
   * troca de filtro colidindo com abertura de drawer).
   */
  const { paramsRef, latestRequestIdRef } = useSealedParamsContext()

  const updateSealedSearchParams = async ({
    pathname,
    newParam
  }: UpdateSealedSearchParamsProps) => {
    if (!pathname) return

    newParam.forEach(([key, value]) => {
      if (value === '') delete paramsRef.current[key]
      else paramsRef.current[key] = value
    })

    const requestId = ++latestRequestIdRef.current

    const sealedRecord: Record<string, string> = {}
    SEALED_FILTER_KEYS.forEach(key => {
      const value = paramsRef.current[key]
      if (value) sealedRecord[key] = value
    })

    const search = new URLSearchParams(window.location.search)
    SEALED_FILTER_KEYS.forEach(key => search.delete(key))
    search.delete(SEALED_FILTERS_PARAM)

    if (Object.keys(sealedRecord).length > 0) {
      try {
        search.set(
          SEALED_FILTERS_PARAM,
          await encryptParamsClient(sealedRecord)
        )
      } catch (error) {
        addError({
          error: error as Error,
          data: {
            context: Context.ACCREDITED_ESTABLISHMENTS,
            code: AccreditedEstablishmentsCode.SEAL_SEARCH_PARAMS_ERROR,
            message: 'Falha ao cifrar os filtros para a URL'
          }
        })
        return
      }
    }

    /*
     * Duas chamadas disparadas perto uma da outra podem ter o `await` acima
     * resolvendo fora de ordem: sem isto, a mais lenta sobrescreveria a URL
     * com um estado mais antigo depois da mais recente ja ter escrito.
     */
    if (requestId !== latestRequestIdRef.current) return

    const searchString = search.toString() ? `?${search.toString()}` : ''
    window.history.replaceState(null, '', `${pathname}${searchString}`)
  }

  return { updateSealedSearchParams }
}

export { useSealedSearchParams }
